import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import st from './ListOfEvents.module.scss'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { superAxiosRequest } from 'axiosApi'
import { useHistory } from 'react-router-dom'
import Loader from 'components/UI/Loader/Loader'
import Pagination from 'components/UI/Pagination/Pagination'

const initPagination = {
  page: 1,
  limit: 10,
}

const objectTypes = [
  { id: 0, title: 'Все типы' },
  { id: 1, title: 'Сделки' },
  { id: 2, title: 'Таски' },
]

const ListOfEvents = () => {
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const [pagination, setPagination] = useState(initPagination)
  const [filter, updateFilter] = useLocalStorageFilter({})
  const history = useHistory()
  const [selectedObjectType, setSelectedObjectType] = useState(
    objectTypes[0].id,
  )
  const [language, setLanguage] = useState('en')
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState({ result: [], totalItems: 0 })
  const [eventFields, setEventFields] = useState([])

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'crm_event',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',
      title: 'List of events',
      employee: 'Employee',
      object: 'Object',
      event: 'Event',
      value_before: 'Value before',
      value_after: 'Value after',
      value: 'Value',
      date_picker: 'Start & Finish Date',
      date: 'Date',
      author: 'Author',
      name: 'Name',
    },
  })

  const dateAfter = new Date()
  const firstDayOfLastMoth = new Date(new Date(dateAfter).setDate(1))
  const lastDayOfLastMoth = new Date(new Date().setDate(0))

  const config = [
    {
      nameState: 'manager',
      sort_key: 'employee',
      placeholder: translate?.employee,
      apiLinkCRM: 'integration/employees/?short_form=true',
      type: 'select',
      label: translate?.employee,
    },
    {
      nameState: 'object_type',
      placeholder: translate?.object,
      values: objectTypes.map((type) => ({
        label: type.title,
        value: type.id,
      })),
      type: 'select',
      label: translate?.object,
    },
    {
      nameState: 'event_type',
      placeholder: translate?.event,
      values: eventFields,
      type: 'select',
      label: translate?.event,
    },
    {
      nameState: 'value',
      placeholder: translate?.value,
      type: 'input',
      label: translate?.value,
    },
    {
      nameState: ['date_from', 'date_until'],
      type: 'date_day_picker',
      defaultDate: {
        after: firstDayOfLastMoth,
        before: lastDayOfLastMoth,
      },
      placeholder: translate?.date_picker,
      label: translate?.date_picker,
    },
  ]

  const handleDropFilter = () => {
    const now = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(now.getDate() - 30)

    updateFilter({
      date_from: thirtyDaysAgo.toISOString().split('T')[0],
      date_until: now.toISOString().split('T')[0],
    })
    closeFilter()
  }

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true)
    }
    superAxiosRequest({
      method: 'get',
      link: 'reports/events',
      crm: true,
      params: {
        ...filter,
        offset: (pagination.page - 1) * pagination.limit,
        limit: pagination.limit,
      },
    })
      .then(({ data }) => {
        const keys = [
          'status_deal',
          'client',
          'description',
          'budget',
          'manager',
          'branch',
          'level',
          'deal_name',
          'city',
          'oral_test_level',
          'training_level',
          'note',
          'program',
          'value',
          'title',
          'creator',
          'assignee',
          'start_task',
          'finish_task',
          'status',
          'task_category',
          'priority',
          'file',
          'deal',
          'icon_name',
          'is_pinned',
          'create',
        ]
        const formattedData = data.result.map((event) => {
          const getValue = (value) => {
            if (!value) return '-'

            if (Array.isArray(value)) {
              return value
                .map((obj) => {
                  if (obj.updated_by) {
                    return obj.updated_by === 'None' ? '' : obj.updated_by
                  }
                  return keys
                    .map((key) =>
                      obj[key] && obj[key] !== 'None' ? obj[key] : '',
                    )
                    .filter((val) => val)
                    .join('; ')
                })
                .join('; ')
            }

            return '-'
          }

          return {
            id: event.id,
            objectId: event.object_id,
            date:
              new Date(event.date).toLocaleDateString('ru-RU') +
              ' ' +
              new Date(event.date).toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
              }),
            object: event.object,
            name: event.name,
            eventType: event.event_type.join(', '),
            manager: event.manager || '',
            valueBefore: getValue(event.value_before),
            valueAfter: getValue(event.value_after),
            ...(event.funnels && { funnels: event.funnels }),
          }
        })
        setData({
          result: formattedData,
          totalItems: data._meta.count_items,
        })
      })
      .catch((error) => {
        console.error('Error fetching events:', error)
      })
      .finally(() => setIsLoading(false))
  }, [filter, pagination, selectedObjectType])

  useEffect(() => {
    superAxiosRequest({
      method: 'get',
      link: 'reports/event_types',
      crm: true,
      params: { lang: language },
    })
      .then(({ data }) => {
        if (Array.isArray(data)) {
          const formattedData = data.map((item) => ({
            label: item.name,
            value: item.field,
          }))
          setEventFields(formattedData)
        } else {
          console.error('Unexpected response format:', data)
        }
      })
      .catch((error) => {
        console.error('Error fetching event types:', error)
      })
  }, [language])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/crm/list-of-events') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const getHref = (row) => {
    if (row.object === 'Task') {
      return '/crm/tasks'
    }
    if (row?.funnels && row.funnels[0]) {
      return `/crm/customer-card/${row.objectId}/?funnel_id=${row.funnels[0]}`
    }
    return '#'
  }

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className={st.header}>
            <div>
              <PageHeader title={translate?.title}></PageHeader>
              <SearchBooks
                onSearch={(text) => {
                  updateFilter({ ...filter, name: text })
                  changeTextValue('name', {
                    key: 'name',
                    label: 'Search',
                    value: text,
                  })
                }}
                onClear={() => {
                  const { name: text, ...obj } = filter
                  updateFilter(obj)
                  setTextValues(
                    filterTextValues.filter((field) => field.key !== 'name'),
                  )
                }}
                value={filter?.name || ''}
                placeholder={'Search...'}
                translate={translate}
              />
            </div>
            {!!filterTextValues.length && (
              <FilterTextValues
                onDelete={(key) => {
                  const { [key]: _, ...obj } = filter
                  updateFilter(obj)
                }}
              />
            )}
            {isOpenFilter && (
              <AsideFilter
                getFilterParams={(e) => {
                  updateFilter(e)
                }}
                config={config}
                filterValue={filter}
                onDropFilter={() => {
                  handleDropFilter()
                }}
                translate={translate}
              />
            )}
          </div>
          <div className={st.table}>
            <div className={st.headerForTable}>
              <div className={st.columns}>
                <span>{translate?.date}</span>
                <span>{translate?.author}</span>
                <span>{translate?.object}</span>
                <span>{translate?.name}</span>
                <span>{translate?.event}</span>
                <span>{translate?.value_before}</span>
                <span>{translate?.value_after}</span>
              </div>
              <div className={st.separator} />
            </div>
            <div className={st.body}>
              {data?.result?.map((row, index) => (
                <div key={index} className={st.row}>
                  <span className={st.dateAndTitle}>{row.date}</span>
                  <span className={st.dateAndTitle}>{row.manager}</span>
                  <span>
                    <a
                      className={st.objLink}
                      href={getHref(row)}
                      target={getHref(row) !== '#' ? '_blank' : undefined}
                    >
                      {row.object}
                    </a>
                  </span>
                  <span className={st.text}>{row.name}</span>
                  <span className={st.text}>{row.eventType}</span>
                  <span className={st.text}>{row.valueBefore}</span>
                  <span className={st.text}>{row.valueAfter}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={st.pagination}>
            {data?.totalItems > 10 && (
              <Pagination
                limit={pagination.limit}
                count={data?.totalItems || 0}
                page={pagination.page}
                onChange={(page) => setPagination({ ...pagination, page })}
                onChangePerPage={(limit) => {
                  setPagination({ page: 1, limit })
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default observer(ListOfEvents)
